(() => {
  const currentScript = document.currentScript;
  if (!currentScript?.src) return;

  const originalScriptUrl = currentScript.src.replace(
    /media-gallery-grouped-3b\.js(?=\?|$)/,
    'media-gallery-original-test.js'
  );
  const markupCache = new Map();

  const installPriorityGuardMarker = (gallery) => {
    if (gallery.querySelector('[data-gallery-grouped-eager-guard="true"]')) return;

    const marker = document.createElement('span');
    marker.hidden = true;
    marker.dataset.galleryGroupedPhase = '3b';
    marker.dataset.galleryGroupedEagerGuard = 'true';
    gallery.append(marker);
  };

  const getSectionId = (gallery) => (
    gallery?.dataset.dynamicProductContent?.replace(/-media-gallery$/, '')
    || gallery?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')
    || null
  );

  const getRequest = (gallery, variantId) => {
    const productSection = gallery.closest('.js-product');
    const variantPicker = productSection?.querySelector('variant-picker');
    const sectionId = variantPicker?.dataset.sectionId || getSectionId(gallery);
    if (!sectionId) return null;

    const url = new URL(
      variantPicker?.dataset.url || window.location.pathname,
      window.location.origin
    );
    url.searchParams.set('variant', variantId);
    url.searchParams.set('sections', sectionId);
    return { sectionId, url };
  };

  const parseResponse = (responseText, sectionId) => {
    const sections = typeof responseText === 'string'
      ? JSON.parse(responseText)
      : responseText;
    const sectionHtml = sections?.[sectionId];
    if (!sectionHtml) {
      throw new Error('Section Rendering returned an empty product section.');
    }

    const renderedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const renderedGallery = renderedDocument.querySelector(
      'media-gallery[data-gallery-grouped-phase="3b"]'
    );
    if (!renderedGallery) {
      throw new Error('The rendered section did not contain a grouped 3B gallery.');
    }
    return renderedGallery;
  };

  const fetchMarkup = (gallery, variantId) => {
    const request = getRequest(gallery, variantId);
    if (!request) {
      return Promise.reject(new Error('The product section ID could not be resolved.'));
    }

    const productId = gallery.dataset.galleryGroupedProductId || window.location.pathname;
    const cacheKey = `${productId}:${request.sectionId}:${variantId}`;
    if (markupCache.has(cacheKey)) return markupCache.get(cacheKey);

    const responsePromise = typeof theme !== 'undefined' && theme.fetchCache?.fetch
      ? theme.fetchCache.fetch(request.url.toString())
      : fetch(request.url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Section Rendering request failed with ${response.status}.`);
        }
        return response.text();
      });

    const promise = responsePromise
      .then((responseText) => parseResponse(responseText, request.sectionId))
      .catch((error) => {
        markupCache.delete(cacheKey);
        throw error;
      });

    markupCache.set(cacheKey, promise);
    return promise;
  };

  const cloneTemplate = (source) => {
    if (!source) return null;
    const container = document.createElement('div');
    container.innerHTML = source.outerHTML;
    return container.firstElementChild;
  };

  const bindFallbackControls = (gallery, controls) => {
    if (!controls || controls.dataset.groupedGalleryFallbackBound === 'true') return;
    controls.dataset.groupedGalleryFallbackBound = 'true';
    controls.addEventListener('click', (event) => {
      const button = event.target.closest?.('.media-ctrl__btn[name]');
      if (!button || !controls.contains(button)) return;

      const items = Array.from(gallery.querySelectorAll('.media-viewer__item'));
      const currentIndex = Math.max(0, Number.parseInt(
        gallery.counterCurrent?.textContent || '1',
        10
      ) - 1);
      const targetIndex = button.name === 'next' ? currentIndex + 1 : currentIndex - 1;
      const target = items[targetIndex];
      if (!target || typeof gallery.customSetActiveMedia !== 'function') return;

      gallery.customSetActiveMedia(target, true);
      setImagePolicy(gallery, gallery.dataset.priorityMediaId);
    });
  };

  const bindFallbackThumbs = (gallery, thumbs) => {
    if (!thumbs || thumbs.dataset.groupedGalleryFallbackBound === 'true') return;
    thumbs.dataset.groupedGalleryFallbackBound = 'true';
    thumbs.addEventListener('click', (event) => {
      const thumb = event.target.closest?.('.media-thumbs__item[data-media-id]');
      if (!thumb || !thumbs.contains(thumb)) return;

      const target = gallery.querySelector(
        `.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`
      );
      if (!target || typeof gallery.customSetActiveMedia !== 'function') return;

      gallery.customSetActiveMedia(target, true);
      customElements.get('media-gallery')?.playActiveMedia?.(target);
      setImagePolicy(gallery, gallery.dataset.priorityMediaId);
    });
  };

  const syncControls = (gallery, renderedGallery) => {
    const currentControls = gallery.querySelector('.media-gallery__viewer > .media-ctrl');
    const nextControls = renderedGallery.querySelector('.media-gallery__viewer > .media-ctrl');

    if (currentControls && nextControls) {
      currentControls.hidden = false;
      currentControls.className = nextControls.className;
      currentControls.innerHTML = nextControls.innerHTML;
    } else if (currentControls) {
      currentControls.hidden = true;
      currentControls.innerHTML = '';
    } else if (nextControls) {
      const clonedControls = nextControls.cloneNode(true);
      gallery.querySelector('.media-gallery__viewer')?.append(clonedControls);
      bindFallbackControls(gallery, clonedControls);
    }

    gallery.controls = gallery.querySelector('.media-gallery__viewer > .media-ctrl');
    gallery.prevBtn = gallery.controls?.querySelector('[name="prev"]') || null;
    gallery.nextBtn = gallery.controls?.querySelector('[name="next"]') || null;
    gallery.counterCurrent = gallery.controls?.querySelector('.media-ctrl__current-item') || null;
    gallery.counterTotal = gallery.controls?.querySelector('.media-ctrl__total-items') || null;
  };

  const syncThumbs = (gallery, renderedGallery) => {
    const currentThumbs = gallery.querySelector('.media-thumbs');
    const nextThumbs = renderedGallery.querySelector('.media-thumbs');

    if (currentThumbs && nextThumbs) {
      currentThumbs.closest('.media-gallery__thumbs')?.removeAttribute('hidden');
      currentThumbs.innerHTML = nextThumbs.innerHTML;
      return currentThumbs;
    }

    const currentWrapper = gallery.querySelector('.media-gallery__thumbs');
    const nextWrapper = renderedGallery.querySelector('.media-gallery__thumbs');
    if (currentWrapper) {
      currentWrapper.hidden = true;
      if (currentThumbs) currentThumbs.innerHTML = '';
    }
    if (nextWrapper) {
      let clonedWrapper = currentWrapper;
      if (!clonedWrapper) {
        clonedWrapper = nextWrapper.cloneNode(true);
        const zoomTemplate = gallery.querySelector('.js-media-zoom-template');
        if (zoomTemplate) {
          zoomTemplate.before(clonedWrapper);
        } else {
          gallery.append(clonedWrapper);
        }
      } else {
        clonedWrapper.hidden = false;
        clonedWrapper.className = nextWrapper.className;
        clonedWrapper.innerHTML = nextWrapper.innerHTML;
      }
      bindFallbackThumbs(gallery, clonedWrapper.querySelector('.media-thumbs'));
    }
    return gallery.querySelector('.media-thumbs');
  };

  const syncZoom = (gallery, renderedGallery) => {
    const currentTemplate = gallery.querySelector('.js-media-zoom-template');
    const nextTemplate = cloneTemplate(
      renderedGallery.querySelector('.js-media-zoom-template')
    );

    if (currentTemplate && nextTemplate) {
      currentTemplate.replaceWith(nextTemplate);
    } else if (currentTemplate) {
      currentTemplate.remove();
    } else if (nextTemplate) {
      gallery.append(nextTemplate);
    }

    if (gallery.galleryModal?.parentElement) gallery.galleryModal.remove();
    const activeTemplate = gallery.querySelector('.js-media-zoom-template');
    gallery.galleryModal = activeTemplate?.content.firstElementChild.cloneNode(true) || null;
  };

  const setImagePolicy = (gallery, selectedMediaId) => {
    gallery.querySelectorAll('.media-viewer__item').forEach((item) => {
      const highPriority = String(item.dataset.mediaId) === String(selectedMediaId);
      item.querySelectorAll('img.product-image').forEach((image) => {
        const loading = highPriority ? 'eager' : 'lazy';
        const priority = highPriority ? 'high' : 'low';
        if (image.loading !== loading) image.loading = loading;
        if (image.fetchPriority !== priority) image.fetchPriority = priority;
        image.dataset.galleryLoadingPolicy = `${loading}-${priority}`;
      });
    });

    gallery.querySelectorAll('.media-thumbs__item img, .gallery-zoom__thumb img').forEach((image) => {
      if (image.loading !== 'lazy') image.loading = 'lazy';
      if (image.fetchPriority !== 'low') image.fetchPriority = 'low';
      image.dataset.galleryLoadingPolicy = 'lazy-low';
    });
    gallery.dataset.priorityMediaId = String(selectedMediaId);
  };

  const refreshState = (gallery, selectedMediaId) => {
    const initialViewerItems = Array.from(gallery.querySelectorAll('.media-viewer__item'));
    const selectedItem = initialViewerItems.find(
      (item) => String(item.dataset.mediaId) === String(selectedMediaId)
    ) || initialViewerItems[0];
    if (!selectedItem) {
      throw new Error('The rendered gallery did not contain any media items.');
    }

    gallery.viewer = gallery.querySelector('.media-viewer');
    gallery.thumbs = gallery.querySelector('.media-thumbs');
    const sizeGalleryEnabled = gallery.getAttribute('data-gallery-size-2b') === 'true';
    if (sizeGalleryEnabled && gallery.viewer?.firstElementChild !== selectedItem) {
      gallery.viewer?.prepend(selectedItem);
    }
    const selectedThumb = gallery.thumbs?.querySelector(
      `[data-media-id="${selectedItem.dataset.mediaId}"]`
    );
    if (
      sizeGalleryEnabled
      && selectedThumb
      && gallery.thumbs?.firstElementChild !== selectedThumb
    ) {
      gallery.thumbs.prepend(selectedThumb);
    }

    const viewerItems = Array.from(gallery.querySelectorAll('.media-viewer__item'));
    gallery.viewerItems = gallery.querySelectorAll('.media-viewer__item');
    gallery.thumbsItems = gallery.querySelectorAll('.media-thumbs__item');
    gallery.zoomLinks = gallery.querySelectorAll('.js-zoom-link');
    gallery.variantMediaData = undefined;
    gallery.mediaGroupMap = null;
    gallery.currentGroup = null;
    gallery.currentItem = selectedItem;
    gallery.currentIndex = viewerItems.indexOf(selectedItem);
    gallery.currentThumb = selectedThumb || null;
    gallery.visibleItems = viewerItems;

    if (viewerItems.length > 1) {
      gallery.viewerItemOffset = viewerItems[1].offsetLeft - viewerItems[0].offsetLeft;
    }
    gallery.viewer?.scrollTo({ left: selectedItem.offsetLeft, behavior: 'auto' });

    if (gallery.counterCurrent) {
      gallery.counterCurrent.textContent = String(gallery.currentIndex + 1);
    }
    if (gallery.counterTotal) gallery.counterTotal.textContent = String(viewerItems.length);
    if (gallery.prevBtn) gallery.prevBtn.disabled = gallery.currentIndex === 0;
    if (gallery.nextBtn) {
      gallery.nextBtn.disabled = gallery.currentIndex === viewerItems.length - 1;
    }
    if (gallery.currentThumb && typeof gallery.setActiveThumb === 'function') {
      gallery.setActiveThumb();
    }

    setImagePolicy(gallery, selectedItem.dataset.mediaId);
    gallery.dataset.visibleMediaCount = String(viewerItems.length);
    return selectedItem;
  };

  const replaceMarkup = (gallery, renderedGallery, variantId, selectedMediaId) => {
    const viewer = gallery.querySelector('.media-viewer');
    const nextViewer = renderedGallery.querySelector('.media-viewer');
    if (!viewer || !nextViewer) {
      throw new Error('The rendered gallery viewer was incomplete.');
    }

    viewer.innerHTML = nextViewer.innerHTML;
    syncControls(gallery, renderedGallery);
    syncThumbs(gallery, renderedGallery);
    syncZoom(gallery, renderedGallery);

    gallery.dataset.galleryGroupedVariantId = String(variantId);
    gallery.dataset.galleryGroupedState = 'active';
    const selectedItem = refreshState(gallery, selectedMediaId);

    if (typeof gallery.customSetActiveMedia === 'function') {
      gallery.customSetActiveMedia(selectedItem, false);
      refreshState(gallery, selectedMediaId);
    }
    if (gallery.dataset.zoomTrigger && typeof gallery.triggerZoomInit === 'function') {
      gallery.triggerZoomInit();
    }
  };

  const navigateToVariant = (variantId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.delete('sections');
    window.location.assign(url.toString());
  };

  const getVariantDetails = (event) => {
    const variantId = event.detail?.variant?.id;
    const mediaId = event.detail?.variant?.featured_media?.id;
    if (!variantId || !mediaId) return null;
    return {
      variantId: String(variantId),
      mediaId: String(mediaId)
    };
  };

  const initializeGallery = (gallery) => {
    if (gallery.dataset.galleryGroupedInitialized === 'true') return;
    gallery.dataset.galleryGroupedInitialized = 'true';
    const section = gallery.closest('.js-product');
    let renderToken = 0;
    let requestCount = 0;

    const initialMediaId = gallery.querySelector(
      '.media-viewer__item.is-current-variant'
    )?.dataset.mediaId;
    if (initialMediaId) refreshState(gallery, initialMediaId);

    document.addEventListener('on:variant:change', (event) => {
      if (!section || !section.contains(event.target)) return;
      const details = getVariantDetails(event);
      if (!details) return;
      if (gallery.dataset.galleryGroupedVariantId === details.variantId) {
        refreshState(gallery, details.mediaId);
        return;
      }

      const token = ++renderToken;
      requestCount += 1;
      gallery.dataset.galleryGroupedState = 'loading';

      fetchMarkup(gallery, details.variantId)
        .then((renderedGallery) => {
          if (token !== renderToken) return;
          replaceMarkup(
            gallery,
            renderedGallery,
            details.variantId,
            details.mediaId
          );
        })
        .catch((error) => {
          if (token !== renderToken) return;
          console.error('[Grouped gallery 3B] Section Rendering failed.', error);
          gallery.dataset.galleryGroupedState = 'fallback-navigation';
          navigateToVariant(details.variantId);
        });
    }, true);

    window.__amazerBathGroupedGallery3B ||= new Map();
    window.__amazerBathGroupedGallery3B.set(
      gallery.dataset.galleryGroupedProductId,
      {
        gallery,
        state: () => {
          const images = Array.from(gallery.querySelectorAll('img'));
          return {
            productId: gallery.dataset.galleryGroupedProductId,
            variantId: gallery.dataset.galleryGroupedVariantId,
            state: gallery.dataset.galleryGroupedState,
            priorityMediaId: gallery.dataset.priorityMediaId,
            visibleMediaCount: gallery.querySelectorAll('.media-viewer__item').length,
            requestCount,
            cacheSize: markupCache.size,
            eagerCount: images.filter((image) => image.loading === 'eager').length,
            lazyCount: images.filter((image) => image.loading === 'lazy').length,
            highCount: images.filter((image) => image.fetchPriority === 'high').length,
            lowCount: images.filter((image) => image.fetchPriority === 'low').length
          };
        }
      }
    );
  };

  document.querySelectorAll(
    'media-gallery[data-gallery-grouped-phase="3b"]'
  ).forEach(installPriorityGuardMarker);

  const originalScript = document.createElement('script');
  originalScript.src = originalScriptUrl;
  originalScript.async = false;
  originalScript.addEventListener('load', () => {
    customElements.whenDefined('media-gallery').then(() => {
      document.querySelectorAll(
        'media-gallery[data-gallery-grouped-phase="3b"]'
      ).forEach(initializeGallery);
    });
  });
  originalScript.addEventListener('error', () => {
    console.error('[Grouped gallery 3B] Failed to load the original media gallery script.');
  });
  document.head.appendChild(originalScript);
})();
